'use client';

import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { DirectCodeEditor } from '@/components/ui/direct-code-editor';
import {
  Code,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  FileText,
  Award,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Target,
  Github,
  Timer,
  Hash,
  BarChart3
} from 'lucide-react';

interface JobAssessment {
  job_id: number;
  assessment: string;
  score: 'poor' | 'average' | 'good';
  snippet: string;
}

interface JobAssessmentsData {
  job_assessments?: JobAssessment[];
  overall_analysis?: string;
  link_to_repo?: string;
}

// Also support direct array format
type JobAssessmentsInput = JobAssessmentsData | JobAssessment[];

interface JobAssessmentsProps {
  data: JobAssessmentsInput | null;
}

const getScoreColor = (score: string) => {
  switch (score) {
    case 'good':
      return 'text-emerald-700';
    case 'average':
      return 'text-amber-700';
    case 'poor':
      return 'text-rose-700';
    default:
      return 'text-gray-700';
  }
};

const getScoreBgColor = (score: string) => {
  switch (score) {
    case 'good':
      return 'bg-emerald-50';
    case 'average':
      return 'bg-amber-50';
    case 'poor':
      return 'bg-rose-50';
    default:
      return 'bg-gray-50';
  }
};

const getScoreBorderColor = (score: string) => {
  switch (score) {
    case 'good':
      return 'border-emerald-200';
    case 'average':
      return 'border-amber-200';
    case 'poor':
      return 'border-rose-200';
    default:
      return 'border-gray-200';
  }
};

const getScoreBadgeColor = (score: string) => {
  switch (score) {
    case 'good':
      return 'bg-emerald-100 text-emerald-800 border-emerald-200 hover:bg-emerald-200';
    case 'average':
      return 'bg-amber-100 text-amber-800 border-amber-200 hover:bg-amber-200';
    case 'poor':
      return 'bg-rose-100 text-rose-800 border-rose-200 hover:bg-rose-200';
    default:
      return 'bg-gray-100 text-gray-800 border-gray-200 hover:bg-gray-200';
  }
};

const getScoreIcon = (score: string) => {
  switch (score) {
    case 'good':
      return <CheckCircle2 className="w-4 h-4" />;
    case 'average':
      return <AlertTriangle className="w-4 h-4" />;
    case 'poor':
      return <XCircle className="w-4 h-4" />;
    default:
      return <AlertTriangle className="w-4 h-4" />;
  }
};

const getScorePercentage = (score: string) => {
  switch (score) {
    case 'good':
      return 85;
    case 'average':
      return 65;
    case 'poor':
      return 35;
    default:
      return 0;
  }
};

const CodeSnippet: React.FC<{ snippet: string; jobId: number }> = ({ snippet, jobId }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!snippet || snippet.trim() === '') {
    return (
      <div className="text-sm text-slate-500 italic bg-slate-50 rounded-lg p-4 border border-slate-200">
        No code snippet provided
      </div>
    );
  }

  return (
    <div className="mt-4">
      <Button
        variant="outline"
        size="sm"
        onClick={() => setIsExpanded(!isExpanded)}
        className="mb-3 text-xs border-slate-200 hover:bg-slate-50"
      >
        {isExpanded ? (
          <ChevronDown className="w-3 h-3 mr-1" />
        ) : (
          <ChevronRight className="w-3 h-3 mr-1" />
        )}
        {isExpanded ? 'Hide' : 'Show'} Code Snippet
      </Button>
      
      {isExpanded && (
        <div className="border border-slate-200 rounded-lg overflow-hidden">
          <div className="h-96">
            <DirectCodeEditor
              language="typescript"
              value={snippet}
              readOnly={true}
              showCursor={false}
              appearance="dark"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export function JobAssessments({ data }: JobAssessmentsProps) {
  // Handle both array format and object format
  let jobAssessmentsArray: any[] = [];
  let overallAnalysis: string | undefined;
  let linkToRepo: string | undefined;

  if (Array.isArray(data)) {
    // Direct array format
    jobAssessmentsArray = data;
  } else if (data && data.job_assessments) {
    // Object format
    jobAssessmentsArray = data.job_assessments;
    overallAnalysis = data.overall_analysis;
    linkToRepo = data.link_to_repo;
  }

  if (!jobAssessmentsArray || jobAssessmentsArray.length === 0) {
    return null;
  }

  // Extract overall_analysis and link_to_repo from the last item if it exists
  const lastItem = jobAssessmentsArray[jobAssessmentsArray.length - 1];
  if (lastItem && ('overall_analysis' in lastItem || 'link_to_repo' in lastItem)) {
    if (lastItem.overall_analysis) {
      overallAnalysis = lastItem.overall_analysis;
    }
    if (lastItem.link_to_repo) {
      linkToRepo = lastItem.link_to_repo;
    }
    // Remove the last item from the array as it's not a job assessment
    jobAssessmentsArray = jobAssessmentsArray.slice(0, -1);
  }

  const jobAssessments = jobAssessmentsArray.filter(
    (job): job is JobAssessment => 
      typeof job === 'object' && 
      job !== null && 
      'job_id' in job && 
      'assessment' in job && 
      'score' in job
  );

  if (jobAssessments.length === 0) {
    return null;
  }

  // Calculate overall score
  const overallScore = jobAssessments.reduce((sum, job) => {
    return sum + getScorePercentage(job.score);
  }, 0) / jobAssessments.length;

  const goodCount = jobAssessments.filter(job => job.score === 'good').length;
  const averageCount = jobAssessments.filter(job => job.score === 'average').length;
  const poorCount = jobAssessments.filter(job => job.score === 'poor').length;

  const overallTintBar = overallScore >= 70 ? 'bg-emerald-500/70' : overallScore >= 50 ? 'bg-amber-500/70' : 'bg-rose-500/70';
  const successPct = (goodCount / jobAssessments.length) * 100;
  const successTintBar = successPct >= 66 ? 'bg-emerald-500/60' : successPct >= 33 ? 'bg-amber-500/60' : 'bg-rose-500/60';

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="rounded-2xl border border-slate-200 bg-white">
        <div className="px-6 py-5 flex items-start justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold text-slate-900">Live Coding Assessment</h2>
            <p className="text-slate-600 text-sm mt-1">Evaluation of coding skills and problem-solving</p>
          </div>
          {linkToRepo && (
            <a
              href={linkToRepo}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 bg-slate-50 hover:bg-white text-sm font-medium text-slate-900 shadow-sm"
              aria-label="Open source repository"
            >
              View Repository
              <Github className="w-4 h-4" />
            </a>
          )}
        </div>
        {/* Compact metrics row */}
        <div className="px-6 pb-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className={`rounded-lg border border-slate-200 bg-white p-4`}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-slate-500">Overall Score</span>
              <span className="text-xs text-slate-500">/100</span>
            </div>
            <div className="text-2xl font-semibold text-slate-900">{Math.round(overallScore)}</div>
            <div className="mt-3 h-2 w-full rounded-full bg-slate-100 overflow-hidden">
              <div
                className={`h-full rounded-full ${overallTintBar}`}
                style={{ width: `${overallScore}%` }}
              />
            </div>
          </div>
          <div className={`rounded-lg border border-slate-200 bg-white p-4`}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-slate-500">Tasks Completed</span>
              <span className="text-xs text-slate-500">count</span>
            </div>
            <div className="text-2xl font-semibold text-slate-900">{jobAssessments.length}</div>
            <div className="mt-3 h-2 w-full rounded-full bg-slate-100 overflow-hidden">
              <div className="h-full rounded-full bg-blue-500/60" style={{ width: '100%' }} />
            </div>
          </div>
          <div className={`rounded-lg border border-slate-200 bg-white p-4`}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-slate-500">Success Rate</span>
              <span className="text-xs text-slate-500">good/{jobAssessments.length}</span>
            </div>
            <div className="text-2xl font-semibold text-slate-900">{goodCount}</div>
            <div className="mt-3 h-2 w-full rounded-full bg-slate-100 overflow-hidden">
              <div
                className={`h-full rounded-full ${successTintBar}`}
                style={{ width: `${successPct}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Insights */}
      <div className="space-y-4">
        <div className="rounded-xl border border-slate-200 bg-white p-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg bg-slate-50 border border-slate-200 flex items-center justify-center flex-shrink-0">
              <BarChart3 className="w-5 h-5 text-slate-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-base font-semibold text-slate-900 mb-3">Performance Breakdown</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <div className="flex items-center justify-between text-sm mb-2">
                    <span className="text-emerald-700">Good</span>
                    <span className="font-medium text-slate-900">{goodCount}</span>
                  </div>
                  <div className="h-2 w-full rounded-full bg-emerald-100 overflow-hidden">
                    <div className="h-full rounded-full bg-emerald-500/70" style={{ width: `${(goodCount / jobAssessments.length) * 100}%` }} />
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between text-sm mb-2">
                    <span className="text-amber-700">Average</span>
                    <span className="font-medium text-slate-900">{averageCount}</span>
                  </div>
                  <div className="h-2 w-full rounded-full bg-amber-100 overflow-hidden">
                    <div className="h-full rounded-full bg-amber-500/70" style={{ width: `${(averageCount / jobAssessments.length) * 100}%` }} />
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between text-sm mb-2">
                    <span className="text-rose-700">Poor</span>
                    <span className="font-medium text-slate-900">{poorCount}</span>
                  </div>
                  <div className="h-2 w-full rounded-full bg-rose-100 overflow-hidden">
                    <div className="h-full rounded-full bg-rose-500/70" style={{ width: `${(poorCount / jobAssessments.length) * 100}%` }} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Detailed Assessments */}
      <div className="space-y-4">
        <h3 className="text-base font-semibold text-slate-900">Detailed Assessments</h3>
        <div className="space-y-4">
          {jobAssessments.map((job) => (
            <div
              key={job.job_id}
              className="rounded-xl border border-slate-200 bg-white p-6 hover:bg-slate-50/50 transition-colors"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-slate-50 border border-slate-200 flex items-center justify-center flex-shrink-0">
                    <Code className="w-5 h-5 text-slate-600" />
                  </div>
                  <div>
                    <div className="flex items-center gap-3 mb-1">
                      <h4 className="text-base font-semibold text-slate-900">Task {job.job_id}</h4>
                      <span className={`px-2 py-0.5 rounded-full border text-xs capitalize ${job.score === 'good' ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : job.score === 'average' ? 'bg-amber-50 border-amber-200 text-amber-700' : 'bg-rose-50 border-rose-200 text-rose-700'}`}>
                        {job.score}
                      </span>
                    </div>
                    <p className="text-slate-700 text-sm leading-relaxed">{job.assessment}</p>
                  </div>
                </div>
              </div>
              <div className="mt-4">
                <CodeSnippet snippet={job.snippet} jobId={job.job_id} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Overall Analysis */}
      {overallAnalysis && (
        <div className="rounded-xl border border-slate-200 bg-white p-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg bg-slate-50 border border-slate-200 flex items-center justify-center flex-shrink-0">
              <FileText className="w-5 h-5 text-slate-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-base font-semibold text-slate-900 mb-2">Overall Analysis</h3>
              <p className="text-slate-700 text-sm leading-relaxed">{overallAnalysis}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
